import React, { useMemo, useState } from "react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useGetNbaPlayerByIdQuery } from "../../store/api/nbaApi";
import PlayerHeadshot from "../../components/PlayerHeadshot";
import PlayerBioSummary from "../../components/PlayerBioSummary";
import CareerArcChart from "../../components/charts/CareerArcChart";
import SimilarSeasons from "../../components/similarSeasons/SimilarSeasons";
import { SIMILAR_SEASONS_ANCHOR } from "../../components/similarSeasons/similarSeasonsFormat";
import { compareChipSx } from "../../components/compare/compareChromeStyles";
import { getResultsGridSx } from "../../utils/resultsGrid";
import { BRAND_COLORS, brandOutlinedButtonSx, brandRgba } from "../../utils/brandAssets";
import { sortYearKeys } from "../../utils/yearKey";
import {
  NBA_STAT_GROUPS,
  buildNbaSeasonChartPoints,
  buildNbaSeasonRows,
  formatNbaStatValue,
  getNbaCareer,
  getNbaChartStats,
  getNbaComparePath,
  getNbaDefaultChartStat,
  getNbaDisplayName,
  getNbaSeasonColumns,
  getNbaSeasonMap,
  playerHasNbaSeasons,
} from "../../adapters/nbaPlayerAdapter";

const StatBlock = ({ label, value }) => (
  <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="h5" fontWeight={800} sx={{ fontVariantNumeric: "tabular-nums" }}>
      {value ?? "—"}
    </Typography>
  </Paper>
);

const NbaPlayerDetails = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: player, isLoading, error } = useGetNbaPlayerByIdQuery(id, {
    skip: !id,
  });
  const [groupId, setGroupId] = useState("totals");
  const [chartStat, setChartStat] = useState(getNbaDefaultChartStat("totals"));
  const [seasonSortModel, setSeasonSortModel] = useState([
    { field: "year", sort: "desc" },
  ]);

  const chartStats = getNbaChartStats(groupId);
  const activeChartStat = chartStats.some((stat) => stat.id === chartStat)
    ? chartStat
    : getNbaDefaultChartStat(groupId);
  const activeChartMeta =
    chartStats.find((stat) => stat.id === activeChartStat) || chartStats[0];

  const seasonSortedField = seasonSortModel[0]?.field;
  const seasonRows = useMemo(
    () => buildNbaSeasonRows(player, groupId),
    [player, groupId]
  );
  const seasonColumns = useMemo(
    () => getNbaSeasonColumns(groupId, seasonSortedField),
    [groupId, seasonSortedField]
  );
  const chartPoints = useMemo(
    () => buildNbaSeasonChartPoints(player, activeChartStat),
    [player, activeChartStat]
  );
  const similarSeasonOptions = useMemo(
    () => sortYearKeys(Object.keys(getNbaSeasonMap(player, "totals")), "desc"),
    [player]
  );
  const seasonFromUrl = searchParams.get("season");
  const selectedSimilarSeason = similarSeasonOptions.includes(seasonFromUrl)
    ? seasonFromUrl
    : similarSeasonOptions[0] || "";

  const handleSimilarSeasonChange = (nextSeason) => {
    const next = new URLSearchParams(searchParams);
    if (nextSeason) {
      next.set("season", nextSeason);
    } else {
      next.delete("season");
    }
    setSearchParams(next, { replace: true });
  };

  const handleGroupChange = (_, nextGroup) => {
    if (!nextGroup) return;
    setGroupId(nextGroup);
    setChartStat(getNbaDefaultChartStat(nextGroup));
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !player) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Player not found.</Alert>
      </Container>
    );
  }

  const totals = getNbaCareer(player, "totals");
  const perGame = getNbaCareer(player, "perGame");
  const bio = player.bio || player.bioData;
  const hasSeasons = playerHasNbaSeasons(player);

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-start", mb: 4 }}>
        <PlayerHeadshot playerId={player.playerId} sport="nba" size={120} />
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="overline" color="text.secondary">
            NBA Player
          </Typography>
          <Typography variant="h3" fontWeight={800}>
            {getNbaDisplayName(player)}
          </Typography>
          {bio?.position && (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              {bio.position}
              {bio.shoots ? ` · ${bio.shoots}` : ""}
            </Typography>
          )}
          <Button
            component={RouterLink}
            to={getNbaComparePath([player.playerId])}
            variant="outlined"
            sx={{ ...brandOutlinedButtonSx, mt: 2 }}
          >
            Compare
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={4} md={2}>
          <StatBlock label="PTS" value={formatNbaStatValue(totals.pts, "count")} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatBlock label="REB" value={formatNbaStatValue(totals.trb, "count")} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatBlock label="AST" value={formatNbaStatValue(totals.ast, "count")} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatBlock label="STL" value={formatNbaStatValue(totals.stl, "count")} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatBlock label="BLK" value={formatNbaStatValue(totals.blk, "count")} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatBlock label="PPG" value={formatNbaStatValue(perGame.pts_per_g, "decimal1")} />
        </Grid>
      </Grid>

      {!hasSeasons ? (
        <Alert severity="info">No season log in this player's archive.</Alert>
      ) : (
        <>
          <Tabs value={groupId} onChange={handleGroupChange} sx={{ mb: 2 }}>
            {NBA_STAT_GROUPS.map((group) => (
              <Tab key={group.id} value={group.id} label={group.label} />
            ))}
          </Tabs>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                mb: 2,
              }}
            >
              <Typography variant="h6" fontWeight={700}>
                {activeChartMeta?.label} by season
              </Typography>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Stat</InputLabel>
                <Select
                  value={activeChartStat}
                  label="Stat"
                  onChange={(event) => setChartStat(event.target.value)}
                >
                  {chartStats.map((stat) => (
                    <MenuItem key={stat.id} value={stat.id}>
                      {stat.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <CareerArcChart
              namedSeries={[
                {
                  name: getNbaDisplayName(player),
                  points: chartPoints,
                },
              ]}
              yAxisName={activeChartMeta?.label}
              emptyMessage={`No season values for ${activeChartMeta?.label} in this player's log.`}
            />
          </Paper>

          <Paper sx={{ overflow: "hidden" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                px: 2,
                pt: 2,
                pb: 1,
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Season log
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Selected season drives Similar Seasons
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {selectedSimilarSeason ? (
                  <Chip
                    label={selectedSimilarSeason}
                    size="small"
                    sx={{
                      ...compareChipSx(true),
                      bgcolor: BRAND_COLORS.cyan,
                      color: BRAND_COLORS.navy,
                      borderColor: BRAND_COLORS.cyan,
                      fontWeight: 700,
                    }}
                  />
                ) : null}
                <Button
                  component="a"
                  href={`#${SIMILAR_SEASONS_ANCHOR}`}
                  size="small"
                  onClick={(event) => {
                    const section = document.getElementById(SIMILAR_SEASONS_ANCHOR);
                    if (!section) return;
                    event.preventDefault();
                    section.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  sx={{ ...brandOutlinedButtonSx, textTransform: "none", fontWeight: 700 }}
                >
                  View similar seasons
                </Button>
              </Box>
            </Box>
            <DataGrid
              rows={seasonRows}
              columns={seasonColumns}
              loading={false}
              disableRowSelectionOnClick
              disableColumnMenu
              hideFooter
              sortingMode="client"
              sortModel={seasonSortModel}
              onSortModelChange={setSeasonSortModel}
              onRowClick={(params) => handleSimilarSeasonChange(params.id)}
              getRowClassName={(params) =>
                params.id === selectedSimilarSeason ? "nba-season-selected" : ""
              }
              rowHeight={52}
              sx={{
                ...getResultsGridSx(),
                cursor: "pointer",
                "& .nba-season-selected": {
                  bgcolor: brandRgba(BRAND_COLORS.cyan, 0.12),
                  boxShadow: `inset 3px 0 0 ${BRAND_COLORS.cyan}`,
                },
              }}
            />
          </Paper>
        </>
      )}

      {hasSeasons && (
        <SimilarSeasons
          league="nba"
          playerId={player.playerId}
          season={selectedSimilarSeason}
          seasons={similarSeasonOptions}
          onSeasonChange={handleSimilarSeasonChange}
        />
      )}

      <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Bio
        </Typography>
        <PlayerBioSummary sport="nba" bio={bio} />
      </Paper>
    </Container>
  );
};

export default NbaPlayerDetails;
